import type {ReactElement} from 'react';
import React from 'react';
import type {ControlledListSeasonalItemProps} from './controlled-list-seasonal-item.types';
import {ControlledListSeasonalItemType} from './controlled-list-seasonal-item.types';
import styles from './controlled-list-seasonal-item.module.scss';
import {ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {noop} from '@Utils/general.util';
import {SeasonalDecorationEmoji} from '@Utils/seasonal-promotion.util';

export function ControlledListSeasonalItem({
  id,
  text,
  seasonType,
  onClick = noop,
  className = '',
  textClassName = '',
  isSelected = false,
  listSize = ControlledListItemSize.SMALL,
  showSelectionCheckmark = true,
  isDisabled = false,
}: ControlledListSeasonalItemProps): ReactElement {
  const getTextColorClass = (): string => {
    if (isDisabled) {
      return 'content-disabled';
    }

    switch (seasonType) {
      case ControlledListSeasonalItemType.HALLOWEEN:
        return 'content-halloween';
      case ControlledListSeasonalItemType.CHRISTMAS:
        return styles.christmasText;
      default:
        return '';
    }
  };

  const getListItemClasses = (): string => {
    const classes = [
      'flex-row-align-center',
      styles.seasonalListItem,
      listSize === ControlledListItemSize.SMALL ? styles.small : styles.medium,
      isSelected ? styles.selected: '',
      isDisabled ? styles.disabled : '',
      'spacing-p-l-3 spacing-p-r-3 _unpadded-bottom',
      'hover-transition-all',
      className,
    ];

    return classes.join(' ');
  };

  const getSeasonalTextDecoration = (): string => {
    switch (seasonType) {
      case ControlledListSeasonalItemType.HALLOWEEN:
        return SeasonalDecorationEmoji.HALLOWEEN;

      case ControlledListSeasonalItemType.CHRISTMAS:
        return SeasonalDecorationEmoji.CHRISTMAS;

      default:
        return '';
    }
  };

  const getTextSize = (): TextSize => {
    switch (listSize) {
      case ControlledListItemSize.MEDIUM:
        return TextSize.SMALL;
      case ControlledListItemSize.SMALL:
      default:
        return TextSize.XSMALL;
    }
  };

  const onClickItem = (event: React.MouseEvent<HTMLLIElement>): void => {
    onClick(id, event);
  };

  return (
    <li id={id} className={getListItemClasses()} onClick={onClickItem}>
      <Text val={getSeasonalTextDecoration()} size={getTextSize()} className="spacing-m-r-1" />
      <Text val={text} className={`spacing-m-r-2 -truncated-text flex-1 ${styles.listItemText} ${getTextColorClass()} ${textClassName}`} size={getTextSize()} bold />
      {showSelectionCheckmark && isSelected ? (
        <Icon icon="icon-check" size={IconSize.SIZE_ICON_20} className={`${styles.selectionIcon} ${isSelected ? 'content-primary' : ''}`} />
      ) : null}
    </li>
  );
}
